import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import ApiService from "../../../../../services/ApiService";
import { useAuth } from "../../../../Providers/LandmarkAuthContext";

const CmsContext = createContext(null);

const API_BASE = "landmark/website";

export function CmsProvider({ children }) {
  const { employee } = useAuth();
  const api = useMemo(() => new ApiService(), []);

  const [config, setConfig] = useState({
    siteUrl: "",
    previewToken: "",
    deployHookConfigured: false,
  });
  const [publishStatus, setPublishStatus] = useState({
    changedDocuments: [],
    changedEntries: [],
    lastRelease: null,
    loaded: false,
  });
  // bump to force the preview iframe to reload after saves
  const [previewVersion, setPreviewVersion] = useState(0);
  const previewTimer = useRef(null);

  const refreshPublishStatus = useCallback(async () => {
    const res = await api.get(`${API_BASE}/publishStatus`);
    if (res && res.success) {
      setPublishStatus({
        changedDocuments: res.changedDocuments || [],
        changedEntries: res.changedEntries || [],
        lastRelease: res.lastRelease || null,
        loaded: true,
      });
    }
    return res;
  }, [api]);

  useEffect(() => {
    (async () => {
      const res = await api.get(`${API_BASE}/config`);
      if (res && res.success) {
        setConfig({
          siteUrl: res.siteUrl || "",
          previewToken: res.previewToken || "",
          deployHookConfigured: !!res.deployHookConfigured,
        });
      }
    })();
    refreshPublishStatus();
  }, [api, refreshPublishStatus]);

  // debounce preview reloads so rapid saves don't thrash the iframe
  const bumpPreview = useCallback(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => {
      setPreviewVersion((v) => v + 1);
    }, 800);
  }, []);

  const changedCount =
    publishStatus.changedDocuments.length + publishStatus.changedEntries.length;

  const previewUrl = useCallback(
    (sitePath) => {
      if (!config.siteUrl || !config.previewToken) return "";
      const base = config.siteUrl.replace(/\/$/, "");
      return `${base}/api/draft?secret=${encodeURIComponent(
        config.previewToken
      )}&slug=${encodeURIComponent(sitePath || "/")}`;
    },
    [config]
  );

  const value = useMemo(
    () => ({
      api,
      API_BASE,
      employee,
      config,
      publishStatus,
      changedCount,
      refreshPublishStatus,
      previewUrl,
      previewVersion,
      bumpPreview,
    }),
    [
      api,
      employee,
      config,
      publishStatus,
      changedCount,
      refreshPublishStatus,
      previewUrl,
      previewVersion,
      bumpPreview,
    ]
  );

  return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>;
}

export function useCms() {
  const ctx = useContext(CmsContext);
  if (!ctx) throw new Error("useCms must be used within CmsProvider");
  return ctx;
}
